// Google reCAPTCHA v3 (invisible, score-based) client helper.
//
// The site key is PUBLIC by design — it ships in the page. It reads from
// NEXT_PUBLIC_RECAPTCHA_SITE_KEY when provided (build arg), else falls back to
// the registered JuicyStarz key so this works without a workflow change. The
// SECRET lives only in the API (Recaptcha:SecretKey) and is never here.
export const RECAPTCHA_SITE_KEY =
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '6LfjKGotAAAAAI690o-yNqmUhv7Cvn0JJfA5mPPG';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.grecaptcha?.execute) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('recaptcha_script_failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    s.setAttribute('data-recaptcha', '1');
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('recaptcha_script_failed'));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/** Warm the script early (e.g. on a form mount) so the token mint is instant. */
export function preloadRecaptcha(): void {
  void loadScript().catch(() => {});
}

/**
 * Mint an action-scoped v3 token. Returns '' if reCAPTCHA can't load — the
 * server soft-passes when the secret is unset, and fails closed once it's set,
 * so an empty token degrades correctly in both states.
 */
export async function getRecaptchaToken(action: string): Promise<string> {
  if (!RECAPTCHA_SITE_KEY) return '';
  try {
    await loadScript();
    const grecaptcha = typeof window !== 'undefined' ? window.grecaptcha : undefined;
    if (!grecaptcha?.execute) return '';
    await new Promise<void>((resolve) => grecaptcha.ready(() => resolve()));
    return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  } catch {
    return '';
  }
}
